/* Solar adviser lifecycle controller. No credentials, DOM, auto-start or retries. */
(function (root) {
  'use strict';
  function createController(options) {
    let session = null, startPromise = null, activeStart = null, endPromise = null, generation = 0, pending = null, cleanupFailed = false;
    let state = 'idle', mode = null, consent = false, muted = false;
    const wait = options.wait || (ms => new Promise(resolve => setTimeout(resolve, ms)));
    const emit = (type, value) => { if (options.onEvent) options.onEvent(type, value); };
    const snapshot = () => ({ state, mode, consent, muted, pending: !!pending });
    const update = value => { state = value; emit('state', snapshot()); };
    const fail = error => {
      pending = null;
      emit('error', error instanceof Error ? error.message : String(error || 'Verbindung fehlgeschlagen.'));
      update('error');
    };
    async function sendCurrent(text, token) {
      if (token !== generation || state !== 'connected' || !session || (session.isOpen && !session.isOpen())) {
        throw new Error('Die Frage wurde nicht gesendet. Bitte starte die Unterhaltung erneut.');
      }
      session.sendUserMessage(text);
      emit('userMessage', { message: text, mode, conversationId: session.getId?.() });
    }
    async function start(nextMode, text) {
      const token = ++generation;
      pending = text || null;
      mode = nextMode;
      muted = false;
      update('connecting');
      const alive = () => token === generation;
      const config = {
        agentId: options.agentId,
        textOnly: nextMode === 'text',
        connectionType: nextMode === 'text' ? 'websocket' : 'webrtc',
        overrides: { agent: { language: 'de' }, conversation: { textOnly: nextMode === 'text' } },
        dynamicVariables: options.dynamicVariables || {},
        clientTools: options.clientTools || {},
        onMessage: message => { if (alive()) emit('message', { ...message, mode }); },
        onAgentChatResponsePart: value => { if (alive() && mode === 'text') emit('messagePart', { ...value, mode }); },
        onAgentResponseCorrection: value => { if (alive()) emit('correction', value); },
        onRichContent: value => { if (alive()) emit('richContent', value); },
        onModeChange: value => { if (alive()) emit('speaking', value.mode === 'speaking'); },
        onError: message => {
          if (!alive()) return;
          const text = String(message || 'Verbindungsfehler.');
          emit('error', text);
          // The pinned SDK returns void for setMicMuted and reports async failure here.
          // Never leave a potentially live microphone labelled as successfully muted.
          if (/Failed to set input muted state/i.test(text)) {
            muted = false;
            emit('error', 'Die Stummschaltung konnte nicht bestätigt werden. Der Anruf wird zur Sicherheit beendet.');
            void end();
          }
        },
        onDisconnect: details => {
          if (!alive()) return;
          generation++;
          session = null;
          pending = null;
          muted = false;
          mode = null;
          update('idle');
          emit('disconnected', details || {});
        }
      };
      try {
        startPromise = options.startSession(config);
        const created = await startPromise;
        startPromise = null;
        if (!alive()) {
          try { await created.endSession(); } catch (error) { cleanupFailed = true; throw error; }
          return false;
        }
        session = created;
        if (created.isOpen && !created.isOpen()) throw new Error('Die Verbindung ist noch nicht bereit. Die Frage wurde nicht gesendet.');
        update('connected');
        emit('connected', { mode, conversationId: created.getId?.() });
        // Same initial-send grace used by the pinned native widget. Never retry a send.
        if (pending) {
          await wait(100);
          if (!alive()) return false;
          const once = pending;
          pending = null;
          await sendCurrent(once, token);
          emit('state', snapshot());
        }
        return true;
      } catch (error) {
        startPromise = null;
        if (alive()) {
          const old = session;
          session = null;
          generation++;
          try { await old?.endSession(); } catch (_) { cleanupFailed = true; update('blocked'); return false; }
          fail(error);
        }
        return false;
      }
    }
    function begin(nextMode, text) {
      const operation = start(nextMode, text);
      activeStart = operation;
      operation.finally(() => { if (activeStart === operation) activeStart = null; });
      return operation;
    }
    async function request(nextMode, text) {
      text = typeof text === 'string' ? text.trim().slice(0, 2000) : '';
      if (nextMode === 'text' && !text) return false;
      if (state === 'blocked' || state === 'connecting' || state === 'disconnecting' || pending) return false;
      if (!consent) { pending = { mode: nextMode, text }; update('consent'); return false; }
      if (session && state === 'connected') {
        if (nextMode === 'voice' && mode === 'text') { emit('voiceSwitchRequired'); return false; }
        if (text) {
          try { await sendCurrent(text, generation); return true; }
          catch (error) { if (await end()) fail(error); return false; }
        }
        return true;
      }
      return begin(nextMode, text);
    }
    function end() {
      if (endPromise) return endPromise;
      if (state === 'blocked') return Promise.resolve(false);
      const operation = performEnd();
      endPromise = operation;
      operation.finally(() => { if (endPromise === operation) endPromise = null; });
      return operation;
    }
    async function performEnd() {
      if (state === 'blocked') return false;
      const old = session, inFlight = activeStart;
      generation++;
      pending = null;
      session = null;
      update('disconnecting');
      try {
        if (old) await old.endSession();
        // start() owns cleanup of a session resolving after cancellation.
        if (inFlight) { try { await inFlight; } catch (_) {} }
        if (cleanupFailed) throw new Error('Session cleanup failed');
      } catch (error) {
        emit('error', 'Das Beenden konnte nicht bestätigt werden. Bitte lade die Seite neu, bevor du einen weiteren Chat startest.');
        update('blocked');
        return false;
      }
      mode = null;
      muted = false;
      update('idle');
      emit('ended');
      return true;
    }
    return {
      snapshot,
      send: text => request('text', text),
      voice: () => request('voice', ''),
      acceptConsent: async () => {
        if (state !== 'consent' || !pending || typeof pending !== 'object') return false;
        const action = pending;
        pending = null;
        consent = true;
        return begin(action.mode, action.text);
      },
      cancelConsent: () => { if (state === 'consent') { pending = null; update('idle'); } },
      switchToVoice: async () => { if (await end()) return request('voice', ''); return false; },
      end,
      toggleMute: () => {
        if (state !== 'connected' || mode !== 'voice' || !session) return;
        // This flag means requested, not verified. There is no success callback in SDK 1.30.0.
        muted = !muted;
        try { session.setMicMuted(muted); }
        catch (_) {
          muted = false;
          emit('error', 'Die Mikrofonsteuerung ist fehlgeschlagen. Der Anruf wird zur Sicherheit beendet.');
          void end();
          return;
        }
        emit('state', snapshot());
      }
    };
  }
  async function ensureNativeReady(options) {
    let cancelled = false, timer;
    const wait = options.wait || (ms => new Promise(resolve => setTimeout(resolve, ms)));
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => { cancelled = true; reject(new Error('Der Originalchat wurde nicht rechtzeitig bereit. Bitte versuche es erneut oder lade die Seite neu.')); }, options.timeoutMs || 12000);
    });
    const operation = (async () => {
      // An existing script tag is not proof that its definition loaded successfully.
      if (!options.isDefined()) await options.loadDefinition();
      await options.whenDefined();
      while (!cancelled) {
        if (options.isUsable()) return true;
        await wait(100);
      }
      return false;
    })();
    try { return await Promise.race([operation, timeout]); }
    finally { cancelled = true; clearTimeout(timer); }
  }
  root.SolarAdviserCore = { createController, ensureNativeReady };
  if (typeof module !== 'undefined' && module.exports) module.exports = root.SolarAdviserCore;
})(typeof globalThis !== 'undefined' ? globalThis : this);
